import GlobalStyles from '@mui/material/GlobalStyles'
import PropTypes from 'prop-types'
import { graphql, useStaticQuery } from 'gatsby'
import React, { useMemo } from 'react'
import { PRIMARY_COLOR_MAPPING, SECONDARY_COLOR_MAPPING } from '../constants'

const getHarborThemeStyles = ({
  linkColor,
  ctaPrimaryLinkColor,
  ctaSecondaryLinkColor,
}) => ({
    ':root .withHarborThemeCta': {
      backgroundColor: ctaPrimaryLinkColor,
      color: ctaSecondaryLinkColor,
    },

    ':root .withHarborThemeWrapper a': {
      color: linkColor,
      textDecoration: 'none',
      '&:hover': { textDecoration: 'underline' },
      '&:focus': { outline: '2px solid blue', outlineOffset: '2px' },
    },

    ':root .withHarborThemeWrapper': {
      display: 'block',
    },

    ':root .withHarborThemeWrapper img': { maxWidth: '100%' },

    // NOTE: removed the stray comma after `ol` that broke this rule
    ':root .withHarborThemeWrapper p:not(:empty), :root .withHarborThemeWrapper ul, :root .withHarborThemeWrapper ol': {
      fontFamily: 'Roboto',
      marginBottom: '8px',
    },

    ':root .withHarborThemeWrapper span': { fontFamily: 'Roboto' },
    ':root .withHarborThemeWrapper p:empty': { margin: 0, padding: 0 },
    ':root .withHarborThemeWrapper .measure': {
      maxWidth: 'none',
      width: 'auto',
      marginLeft: 0,
      marginRight: 0,
    },

    ':root .withHarborThemeWrapper h1, :root .withHarborThemeWrapper h2, :root .withHarborThemeWrapper h3, :root .withHarborThemeWrapper h4, :root .withHarborThemeWrapper h5, :root .withHarborThemeWrapper h6': {
      fontFamily: 'Poppins',
      margin: '8px 0',
    },

    ':root .withHarborThemeWrapper h1[class*="paint"], \
     :root .withHarborThemeWrapper h2[class*="paint"], \
     :root .withHarborThemeWrapper h3[class*="paint"], \
     :root .withHarborThemeWrapper h4[class*="paint"], \
     :root .withHarborThemeWrapper h5[class*="paint"], \
     :root .withHarborThemeWrapper h6[class*="paint"]': {
      width: 'unset !important',
      display: 'inline !important',
    },

    ':root .withHarborThemeWrapper hr': {
      width: '100%',
      height: '1px',
      color: 'rgb(9, 42, 72)',
    },

    /* --- Ordered list hierarchy: 1. / a. / i. / 1. --- */
    ':root .withHarborThemeWrapper ol': {
      listStyleType: 'decimal',
      paddingLeft: '1.5rem',
      marginLeft: 0,
    },
    ':root .withHarborThemeWrapper ol ol': {
      listStyleType: 'lower-alpha',
      marginTop: '4px',
      paddingLeft: '1.5rem',
    },
    ':root .withHarborThemeWrapper ol ol ol': {
      listStyleType: 'lower-roman',
      marginTop: '4px',
      paddingLeft: '1.5rem',
    },
    ':root .withHarborThemeWrapper ol ol ol ol': {
      listStyleType: 'decimal', // cycle back if you go this deep
      marginTop: '4px',
      paddingLeft: '1.5rem',
    },

    /* --- Unordered list hierarchy for consistency --- */
    ':root .withHarborThemeWrapper ul': {
      listStyleType: 'disc',
      paddingLeft: '1.5rem',
      marginLeft: 0,
    },
    ':root .withHarborThemeWrapper ul ul': {
      listStyleType: 'circle',
      marginTop: '4px',
      paddingLeft: '1.5rem',
    },
    ':root .withHarborThemeWrapper ul ul ul': {
      listStyleType: 'square',
      marginTop: '4px',
      paddingLeft: '1.5rem',
    },

    /* Optional: tighten spacing between list items for dense lists */
    ':root .withHarborThemeWrapper li + li': {
      marginTop: '4px',
    },
  })


export const HarborCsThemeProvider = ({ children }) => {
  const data = useStaticQuery(graphql`
    query {
      contentstackTheme {
        default_link_color {
          link_color
        }
        default_cta_color {
          cta_color
        }
      }
    }
  `)

  const theme = useMemo(
    () => ({
      linkColor:
        PRIMARY_COLOR_MAPPING[
          data.contentstackTheme?.default_link_color?.link_color
        ] || PRIMARY_COLOR_MAPPING.defaultLinkColor,
      ctaPrimaryLinkColor:
        PRIMARY_COLOR_MAPPING[
          data.contentstackTheme?.default_cta_color?.cta_color
        ] || PRIMARY_COLOR_MAPPING.defaultCtaPrimaryColor,
      ctaSecondaryLinkColor:
        SECONDARY_COLOR_MAPPING[
          data.contentstackTheme?.default_cta_color?.cta_color
        ] || SECONDARY_COLOR_MAPPING.defaultCtaSecondaryColor,
    }),
    [data]
  )

  if (!data?.contentstackTheme) {
    console.warn('HarborCsThemeProvider query returned no data.')
    return <>{children}</> // Fallback rendering
  }

  return (
    <>
      <GlobalStyles styles={getHarborThemeStyles(theme)} />
      {children}
    </>
  )
}

HarborCsThemeProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

export default HarborCsThemeProvider